import React from 'react'
import { motion, HTMLMotionProps } from 'framer-motion'
import { clsx } from 'clsx'

type MotionButtonProps = Omit<HTMLMotionProps<'button'>, 'ref'>

interface ButtonProps extends MotionButtonProps {
  variant?: 'default' | 'outline' | 'ghost' | 'destructive'
  size?: 'default' | 'sm' | 'lg' | 'icon'
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = 'default', size = 'default', ...props }, ref) => {
    const baseClasses = clsx(
      // Base styles
      'inline-flex items-center justify-center rounded-lg font-medium transition-all duration-200',
      'focus:outline-none focus:ring-2 focus:ring-carnival-400 focus:ring-offset-2',
      'disabled:pointer-events-none disabled:opacity-50',
      'active:scale-95',
      
      // Size variants
      {
        'h-10 px-4 py-2': size === 'default',
        'h-8 px-3 py-1 text-sm': size === 'sm',
        'h-12 px-6 py-3 text-lg': size === 'lg',
        'h-10 w-10': size === 'icon',
      },
      
      // Variant styles
      {
        // Default variant
        'bg-carnival-400 text-carnival-900 shadow-outdoor-md hover:bg-carnival-500': 
          variant === 'default',
        
        // Outline variant
        'border border-slate-300 dark:border-slate-600 bg-transparent text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800': 
          variant === 'outline',
        
        // Ghost variant  
        'bg-transparent text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800': 
          variant === 'ghost',
        
        // Destructive variant
        'bg-red-500 text-white shadow-outdoor-md hover:bg-red-600': 
          variant === 'destructive',
      },
      
      className
    )

    return (
      <motion.button
        whileHover={{ scale: size === 'icon' ? 1.05 : 1.02 }}
        whileTap={{ scale: 0.95 }}
        className={baseClasses}
        ref={ref}
        {...props}
      />
    )
  }
)

Button.displayName = 'Button'